import React from 'react';
import { Icon } from '@iconify/react';
import type { UniBoxEntity } from './types';
import { getInitials } from '../../utils/helpers';

/**
 * LeadAvatar — the composite person-picture + company-logo element used in the
 * Sales Intelligence preview surfaces (Send-plan header). Mirrors the Lead
 * Intelligence ViewCard look: a round person avatar with a small company-logo
 * badge, so the two modules read identically for the same lead.
 *
 *  - Person avatar: `entity.avatar` when present, else initials on a dark tile.
 *  - Company logo: resolved from `entity.company_logo` / `entity.company_website`
 *    (Clearbit fallback by domain); hidden cleanly when unavailable.
 *
 * Image loads are per-instance and failure-safe (a bad URL never disables a
 * sibling tile) — see `CompanyLogoImg` / the avatar `onError` fallback.
 */

/** Resolve the best company-logo URL for a lead: a forwarded logo URL wins,
 *  otherwise derive a Clearbit URL from the company website/domain. */
export const resolveCompanyLogoUrl = (entity: UniBoxEntity): string | null => {
  const direct = typeof entity.company_logo === 'string' ? entity.company_logo.trim() : '';
  if (direct) return direct;
  const website = typeof entity.company_website === 'string' ? entity.company_website.trim() : '';
  if (website) {
    try {
      const host = new URL(website.startsWith('http') ? website : `https://${website}`).hostname.replace(/^www\./, '');
      if (host) return `https://logo.clearbit.com/${host}`;
    } catch { /* ignore malformed website */ }
  }
  return null;
};

/** Company logo tile — collapses to nothing on error (no broken-image glyph). */
export const CompanyLogoImg: React.FC<{ src: string; alt: string; className?: string }> = ({ src, alt, className = '' }) => {
  const [errored, setErrored] = React.useState(false);
  React.useEffect(() => { setErrored(false); }, [src]);
  if (errored) return null;
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setErrored(true)}
      className={`shrink-0 rounded-md bg-white/[0.04] object-cover ${className}`}
    />
  );
};

export interface LeadAvatarProps {
  entity: UniBoxEntity;
  /** Person avatar diameter (Tailwind h/w classes). Default h-12 w-12. */
  avatarSize?: string;
  /** Company-logo badge diameter (Tailwind h/w classes). Default h-7 w-7. */
  logoSize?: string;
  className?: string;
}

/**
 * The composite avatar+logo element. The logo badge sits at the bottom-right of
 * the person avatar (overlapping, like a presence badge) — a compact, single-
 * column footprint that fits the Send-plan header.
 */
export const LeadAvatar: React.FC<LeadAvatarProps> = ({ entity, avatarSize = 'h-12 w-12', logoSize = 'h-7 w-7', className = '' }) => {
  const logoSrc = resolveCompanyLogoUrl(entity);
  const avatarSrc = typeof entity.avatar === 'string' ? entity.avatar.trim() : '';
  const [avatarErrored, setAvatarErrored] = React.useState(false);
  React.useEffect(() => { setAvatarErrored(false); }, [avatarSrc, entity.lvl1_id]);

  const name = entity.client_name || entity.company || '?';

  return (
    <div className={`relative shrink-0 ${avatarSize} ${className}`}>
      {/* Person avatar (picture or initials fallback). */}
      {avatarSrc && !avatarErrored ? (
        <img
          src={avatarSrc}
          alt={name}
          loading="lazy"
          onError={() => setAvatarErrored(true)}
          className={`h-full w-full rounded-full object-cover grayscale ring-1 ring-white/10`}
        />
      ) : (
        <div className={`flex h-full w-full items-center justify-center rounded-full bg-[linear-gradient(145deg,#111216,#09090c)] text-sm font-black text-white/70 ring-1 ring-white/10`}>
          {getInitials(name)}
        </div>
      )}
      {/* Company-logo badge bottom-right (only when a logo resolves). */}
      {logoSrc && (
        <div className={`absolute -bottom-1 -right-1 ${logoSize} overflow-hidden rounded-full border border-white/15 bg-white shadow-[0_4px_10px_rgba(0,0,0,0.4)]`}>
          <CompanyLogoImg src={logoSrc} alt={entity.company || name} className={`h-full w-full rounded-full p-1`} />
        </div>
      )}
    </div>
  );
};

export default LeadAvatar;
